/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Swal from "sweetalert2";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import StatTile from "shell/StatTile";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);

export default function SuperAdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [churches, setChurches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editChurch, setEditChurch] = useState(null);
  const [saving, setSaving] = useState(false);

  const authToken = token || localStorage.getItem("authToken");
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${authToken}`,
  };

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [statsRes, churchesRes] = await Promise.all([
        fetch(`${API_BASE}/super-admin/stats`, { headers }),
        fetch(`${API_BASE}/super-admin/churches`, { headers }),
      ]);
      const statsJson = await statsRes.json();
      const churchesJson = await churchesRes.json();
      setStats(statsJson.data || null);
      setChurches(churchesJson.data || []);
    } catch (err) {
      console.error("Failed to load super-admin data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCredentials = async (church) => {
    try {
      const res = await fetch(`${API_BASE}/super-admin/churches/${church.id}`, { headers });
      const json = await res.json();
      setEditChurch({
        ...(json.data || church),
        sms_api_token: "",
        mail_password: "",
        paystack_secret_key: "",
      });
      setEditOpen(true);
    } catch (err) {
      Swal.fire({ title: "Error", text: "Could not load church details", icon: "error" });
    }
  };

  const saveCredentials = async () => {
    setSaving(true);
    try {
      const body = {
        sms_sender_id: editChurch.sms_sender_id || null,
        mail_host: editChurch.mail_host || null,
        mail_port: editChurch.mail_port || null,
        mail_username: editChurch.mail_username || null,
        mail_encryption: editChurch.mail_encryption || null,
        mail_from_address: editChurch.mail_from_address || null,
        mail_from_name: editChurch.mail_from_name || null,
        paystack_public_key: editChurch.paystack_public_key || null,
      };
      // Only send secrets if the super admin actually typed a new value —
      // blank means "leave the stored one unchanged".
      if (editChurch.sms_api_token) body.sms_api_token = editChurch.sms_api_token;
      if (editChurch.mail_password) body.mail_password = editChurch.mail_password;
      if (editChurch.paystack_secret_key) body.paystack_secret_key = editChurch.paystack_secret_key;

      const res = await fetch(`${API_BASE}/super-admin/churches/${editChurch.id}/credentials`, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => null);
        throw new Error(errJson?.message || `HTTP ${res.status}`);
      }
      Swal.fire({
        title: "Saved",
        text: "Church credentials updated",
        icon: "success",
        timer: 1500,
      });
      setEditOpen(false);
      fetchAll();
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message, icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { Header: "Church", accessor: "name", align: "left", width: "25%", Cell: SerialCell },
    {
      Header: "Members",
      accessor: "members_count",
      align: "center",
      width: "10%",
      Cell: SerialCell,
    },
    { Header: "Users", accessor: "users_count", align: "center", width: "10%", Cell: SerialCell },
    {
      Header: "SMS Balance",
      accessor: "sms_balance",
      align: "center",
      width: "12%",
      Cell: SerialCell,
    },
    {
      Header: "SMS Token",
      accessor: "has_sms_token",
      align: "center",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          size="small"
          label={value ? "Set" : "Not set"}
          color={value ? "success" : "default"}
        />
      ),
    },
    {
      Header: "Paystack",
      accessor: "has_paystack",
      align: "center",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          size="small"
          label={value ? "Set" : "Not set"}
          color={value ? "success" : "default"}
        />
      ),
    },
    {
      Header: "Email Setup",
      accessor: "has_mail_setup",
      align: "center",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          size="small"
          label={value ? "Set" : "Not set"}
          color={value ? "success" : "default"}
        />
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      width: "8%",
      Cell: ({ value }) => (
        <Chip size="small" label={value} color={value === "active" ? "success" : "error"} />
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      width: "15%",
      Cell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Icon fontSize="small">settings</Icon>}
          onClick={() => openCredentials(row.original)}
        >
          Credentials
        </Button>
      ),
    },
  ];

  if (isLoading && !stats) {
    return (
      <Box pt={6} pb={3} textAlign="center">
        <Typography>Loading platform overview...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Super Admin" subtitle="Cross-church overview, SMS + email credentials" />

      {stats && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              icon="church"
              label="Churches"
              value={stats.total_churches}
              sub={`${stats.active_churches} active`}
              tone="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              icon="group"
              label="Total Members"
              value={stats.total_members.toLocaleString()}
              sub="across all churches"
              tone="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              icon="payments"
              label="Total Giving"
              value={`GHS ${Number(stats.total_payments_amount).toLocaleString()}`}
              sub="all-time, all churches"
              tone="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              icon="sms"
              label="SMS Sent"
              value={stats.total_sms_sent.toLocaleString()}
              sub="all-time, all churches"
              tone="purple"
            />
          </Grid>
        </Grid>
      )}

      <Card>
        <DataTable
          table={{ columns, rows: churches }}
          isSorted={false}
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 25, 50] }}
          showTotalEntries
          noEndBorder
          canSearch
          searchLabel="Search churches..."
        />
      </Card>

      <FormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={editChurch ? `Credentials — ${editChurch.name}` : "Credentials"}
        onSave={saveCredentials}
        saveLabel={saving ? "Saving..." : "Save"}
        loading={saving}
        maxWidth="md"
      >
        {editChurch && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              SMS
            </Typography>
            <FormSection>
              <TextField
                label="Sender ID"
                value={editChurch.sms_sender_id || ""}
                onChange={(e) => setEditChurch({ ...editChurch, sms_sender_id: e.target.value })}
                fullWidth
                margin="normal"
                helperText="Up to 11 alphanumeric characters, unique across all churches"
              />
              <TextField
                label="API Token"
                type="password"
                value={editChurch.sms_api_token || ""}
                onChange={(e) => setEditChurch({ ...editChurch, sms_api_token: e.target.value })}
                fullWidth
                margin="normal"
                placeholder={
                  editChurch.has_sms_token ? "•••••••• (set — leave blank to keep)" : "Not set"
                }
                helperText="Overrides the platform-wide SMS gateway token for this church only"
              />
            </FormSection>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Paystack
            </Typography>
            <FormSection>
              <TextField
                label="Public Key"
                value={editChurch.paystack_public_key || ""}
                onChange={(e) =>
                  setEditChurch({ ...editChurch, paystack_public_key: e.target.value })
                }
                fullWidth
                margin="normal"
                helperText="This church's own Paystack public key"
              />
              <TextField
                label="Secret Key"
                type="password"
                value={editChurch.paystack_secret_key || ""}
                onChange={(e) =>
                  setEditChurch({ ...editChurch, paystack_secret_key: e.target.value })
                }
                fullWidth
                margin="normal"
                placeholder={
                  editChurch.has_paystack ? "•••••••• (set — leave blank to keep)" : "Not set"
                }
                helperText="Overrides the platform-wide Paystack account for this church only"
              />
            </FormSection>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Email (SMTP)
            </Typography>
            <FormSection>
              <TextField
                label="SMTP Host"
                value={editChurch.mail_host || ""}
                onChange={(e) => setEditChurch({ ...editChurch, mail_host: e.target.value })}
                fullWidth
                margin="normal"
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Port"
                  value={editChurch.mail_port || ""}
                  onChange={(e) => setEditChurch({ ...editChurch, mail_port: e.target.value })}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  select
                  label="Encryption"
                  value={editChurch.mail_encryption || "tls"}
                  onChange={(e) =>
                    setEditChurch({ ...editChurch, mail_encryption: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                >
                  {["tls", "ssl"].map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <TextField
                label="SMTP Username"
                value={editChurch.mail_username || ""}
                onChange={(e) => setEditChurch({ ...editChurch, mail_username: e.target.value })}
                fullWidth
                margin="normal"
              />
              <TextField
                label="SMTP Password"
                type="password"
                value={editChurch.mail_password || ""}
                onChange={(e) => setEditChurch({ ...editChurch, mail_password: e.target.value })}
                fullWidth
                margin="normal"
                placeholder={
                  editChurch.has_mail_setup ? "•••••••• (set — leave blank to keep)" : "Not set"
                }
              />
              <TextField
                label="From Address"
                type="email"
                value={editChurch.mail_from_address || ""}
                onChange={(e) =>
                  setEditChurch({ ...editChurch, mail_from_address: e.target.value })
                }
                fullWidth
                margin="normal"
              />
              <TextField
                label="From Name"
                value={editChurch.mail_from_name || ""}
                onChange={(e) => setEditChurch({ ...editChurch, mail_from_name: e.target.value })}
                fullWidth
                margin="normal"
              />
            </FormSection>
          </>
        )}
      </FormDialog>
    </Box>
  );
}
